import * as React from 'react';
import {connect} from 'react-redux';

import {Accounts} from 'nutshell-core/accounts';
import * as Contacts from 'nutshell-core/contacts';
import * as Leads from 'nutshell-core/leads';
import type {CoreEntityType} from 'nutshell-core/types';
import {getTypeFromApiId} from 'nutshell-core/utils/get-type-from-api-id';

import {EntityPill} from 'shells/entity-pill/entity-pill';

import {EntitySearch} from '../../../core/search/entity-search';

import type {NutshellState} from '../../../../store';

type OwnProps = {
    types?: CoreEntityType[];
    value: string | null | undefined;
    onClearValue: () => void;
    onChange: (id: string) => void;
    autoFocus?: boolean;
    hierarchyTypeFilter?: string;
};
type StateProps = {
    entity:
        | {
              id: string;
              name: string;
              type: string;
          }
        | null
        | undefined;
};
type DispatchProps = {
    fetchEntity: (entityId: string) => void;
};
type Props = OwnProps & StateProps & DispatchProps;

function mapStateToProps(state: NutshellState, ownProps: OwnProps): StateProps {
    const defaultEntity = {entity: null} as const;
    const apiId = ownProps.value;
    if (!apiId) return defaultEntity;
    const entityType = getTypeFromApiId(apiId);

    let entity;
    if (entityType === 'accounts') {
        entity = Accounts.getAccount(state, apiId);
    } else if (entityType === 'leads') {
        entity = Leads.getLead(state, apiId);
    } else if (entityType === 'contacts') {
        entity = Contacts.getContact(state, apiId);
    }

    if (entity) {
        return {
            entity: {
                id: apiId,
                name: entity.name,
                type: entity.type,
            },
        };
    }

    return defaultEntity;
}

// @ts-expect-error
function mapDispatchToProps(dispatch): DispatchProps {
    return {
        fetchEntity: (apiId: string) => {
            const entityType = getTypeFromApiId(apiId);
            if (entityType === 'leads') {
                dispatch(Leads.fetchLeadById(apiId));
            } else if (entityType === 'accounts') {
                dispatch(Accounts.fetchById(apiId));
            } else if (entityType === 'contacts') {
                dispatch(Contacts.fetchById(apiId));
            }
        },
    };
}

type State = {
    entities: {
        [id: string]: {
            name: string;
            type: string;
        };
    };
};

class EntitySelectComponent extends React.Component<Props, State> {
    static defaultProps = {
        types: ['leads', 'contacts', 'accounts'],
    };

    constructor(props: Props) {
        super(props);

        this.state = {
            entities: props.entity ? this.addEntity({}, props.entity) : {},
        };
    }

    componentDidMount() {
        if (this.props.value) {
            this.props.fetchEntity(this.props.value);
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (nextProps.entity && !this.props.entity) {
            this.addEntityToState(
                nextProps.entity.id,
                nextProps.entity.name,
                nextProps.entity.type
            );
        }
    }

    render() {
        const selectedEntityId = this.props.value ? this.props.value : undefined;

        return (
            <div className='ui-entity-searchable-list'>
                {selectedEntityId &&
                selectedEntityId.length &&
                this.state.entities[selectedEntityId] ? (
                    <EntityPill
                        isSmall={true}
                        isLinkable={false}
                        onRemove={this.handleRemove}
                        type={this.state.entities[selectedEntityId].type}
                        name={this.state.entities[selectedEntityId].name}
                    />
                ) : undefined}
                <EntitySearch
                    types={this.props.types}
                    focusOnMount={this.props.autoFocus}
                    placeholder={this.renderPlaceholderText()}
                    onSelect={this.handleSelect}
                    hierarchyTypeFilter={this.props.hierarchyTypeFilter}
                />
            </div>
        );
    }

    renderPlaceholderText(): string {
        if (this.props.types && this.props.types.length === 1) {
            const entityText = {
                leads: 'leads',
                accounts: 'companies',
                contacts: 'people',
                // @ts-expect-error
            }[this.props.types[0]];

            return `Search ${entityText}…`;
        }

        return 'Search…';
    }

    handleSelect = (searchResult: any) => {
        const backboneEntity = searchResult.getEntity();

        if (backboneEntity) {
            this.addEntityToState(
                backboneEntity.id,
                searchResult.attributes.displayName,
                backboneEntity.attributes.type
            );
            this.props.onChange(backboneEntity.id);
        }
    };

    addEntityToState = (id: string, name: string, type: string) => {
        this.setState((prevState: State) => {
            return {
                entities: {
                    ...prevState.entities,
                    [id]: {name, type},
                },
            };
        });
    };

    addEntity = (
        entities: any,
        entity: {
            id: string;
            name: string;
            type: string;
        }
    ) => {
        return {
            ...entities,
            [entity.id]: {name: entity.name, type: entity.type},
        };
    };

    handleRemove = () => {
        this.props.onClearValue();
    };
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps);
// @ts-expect-error
export const EntitySelect = connector(EntitySelectComponent);
